'use client'
import { useEffect, useRef } from 'react'

import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { trackReferralError, trackReferralUrlLanding } from '@src/telemetry/referral-telemetry'

// Yotpo/Swell referral share links (rwrd.io/<code>) redirect to the storefront with a referral
// tracking param. `sref_id` is the one observed on live Quince Yotpo referral URLs (ULU-335); `sref`
// is Swell's legacy variant. We match either so the landing step still fires if the format differs,
// and we record which key matched (never its value) so a wrong assumption is visible in the data.
const REFERRAL_URL_PARAMS = ['sref_id', 'sref'] as const
const TRAFFIC_SOURCE_PARAM = 'trafficSource'

/**
 * Fires the `referral_url_landing` funnel event once when the current URL carries a recognized Yotpo
 * referral marker. Mounted globally (`_app`) so it fires wherever a referred shopper actually lands,
 * not only on the referrer-facing refer-and-earn page. Safe on the server (no-op) and guarded so it
 * emits at most once per mount.
 *
 * @param source label for the surface mounting the hook (e.g. 'storefront').
 */
export const useReferralUrlLandingTelemetry = (source: string): void => {
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (!isBrowser() || hasFiredRef.current) return

    try {
      const params = new URLSearchParams(window.location.search)
      const matchedParam = REFERRAL_URL_PARAMS.find(key => params.get(key))
      if (!matchedParam) return

      hasFiredRef.current = true
      trackReferralUrlLanding({
        source,
        param: matchedParam,
        pathname: window.location.pathname,
        trafficSource: params.get(TRAFFIC_SOURCE_PARAM),
      })
    } catch (error) {
      hasFiredRef.current = true
      trackReferralError('landing', error, { source })
    }
  }, [source])
}

export default useReferralUrlLandingTelemetry
